// @@TASK_LISTS_v100@@ To-dos home: every list as a card (Basecamp-style, with
// progress and a peek at the next items) + a "My assignments" tab.
import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { TodoRow, setDone, useUndoToast, fmtDue } from '../components/TodoBits';

function Progress({ open, done }) {
  const total = (open || 0) + (done || 0);
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div className="hq-todo-progress" title={`${done} of ${total} done`}>
      <div className="hq-todo-bar"><span style={{ width: pct + '%' }} /></div>
      <small>{total ? `${done}/${total} completed` : 'Empty'}</small>
    </div>
  );
}

function ListCard({ list, drag }) {
  return (
    <div className={'hq-todo-card' + (list.inbox ? ' is-inbox' : '') + (drag && drag.over ? ' is-drop-' + drag.over : '')}
      draggable={!!drag} onDragStart={drag && drag.onDragStart} onDragEnd={drag && drag.onDragEnd}
      onDragOver={drag && drag.onDragOver} onDrop={drag && drag.onDrop}>
      <Link to={`/tasks/lists/${list.id}`} className="hq-todo-card-link">
        <div className="hq-todo-card-head">
          <h3>{list.inbox && <span aria-hidden="true">📥 </span>}{list.name}</h3>
          {list.inbox && list.open_count > 0 && <span className="hq-todo-count">{list.open_count}</span>}
        </div>
        {list.description && <p className="hq-todo-card-desc">{list.description}</p>}
        <Progress open={list.open_count} done={list.done_count} />
        <ul className="hq-todo-peek">
          {(list.preview || []).map((p) => (
            <li key={p.id}><span className="hq-todo-peek-dot" />{p.title || '(untitled)'}
              {p.due_date && <small> · {fmtDue(p.due_date)}</small>}</li>
          ))}
          {(!list.preview || !list.preview.length) && <li className="hq-todo-peek-empty">{list.open_count === 0 && list.done_count > 0 ? 'All done 🎉' : 'Nothing here yet'}</li>}
        </ul>
      </Link>
    </div>
  );
}

export default function Tasks() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'mine' ? 'mine' : 'lists';
  const [data, setData] = useState(null);
  const [archived, setArchived] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', description: '' });
  const [msg, setMsg] = useState('');
  const [dragId, setDragId] = useState(null);
  const [over, setOver] = useState(null); // { id, pos: 'before'|'after' }
  // My assignments
  const [people, setPeople] = useState([]);
  const [who, setWho] = useState('');
  const [mine, setMine] = useState(null);
  const [crossing, setCrossing] = useState({});
  const [toast, showToast] = useUndoToast();

  const load = useCallback(async () => {
    const res = await apiFetch('/api/tasks/lists');
    if (res.ok) setData(await res.json());
  }, []);
  const loadArchived = useCallback(async () => {
    const res = await apiFetch('/api/tasks/lists?archived=1');
    if (res.ok) setArchived((await res.json()).lists);
  }, []);
  const loadMine = useCallback(async () => {
    const res = await apiFetch('/api/tasks/mine' + (who ? `?user_id=${encodeURIComponent(who)}` : ''));
    if (res.ok) setMine((await res.json()).tasks);
  }, [who]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (showArchived) loadArchived(); }, [showArchived, loadArchived]);
  useEffect(() => { if (tab === 'mine') loadMine(); }, [tab, loadMine]);
  useEffect(() => {
    apiFetch('/api/tasks/assignees').then((r) => (r.ok ? r.json() : null)).then((d) => d && setPeople(d.assignees || [])).catch(() => {});
  }, []);

  async function createList(e) {
    e.preventDefault();
    setMsg('');
    const res = await apiFetch('/api/tasks/lists', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
    });
    const d = await res.json().catch(() => ({}));
    if (res.ok) navigate(`/tasks/lists/${d.list.id}`);
    else setMsg(d.error || 'Could not create the list.');
  }

  async function unarchive(id) {
    await apiFetch(`/api/tasks/lists/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ archived: false }) });
    loadArchived(); load();
  }

  // ── drag lists to reorder ──
  function dragProps(list) {
    return {
      over: over && over.id === list.id && dragId !== list.id ? over.pos : null,
      onDragStart: (e) => { setDragId(list.id); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', list.id); } catch (_) { /* */ } },
      onDragEnd: () => { setDragId(null); setOver(null); },
      onDragOver: (e) => {
        if (!dragId) return;
        e.preventDefault();
        const r = e.currentTarget.getBoundingClientRect();
        const pos = (e.clientY - r.top) < r.height / 2 && (e.clientX - r.left) < r.width / 2 ? 'before' : 'after';
        if (!over || over.id !== list.id || over.pos !== pos) setOver({ id: list.id, pos });
      },
      onDrop: async (e) => {
        e.preventDefault();
        if (!dragId || dragId === list.id) { setDragId(null); setOver(null); return; }
        const ids = data.lists.map((l) => l.id).filter((id) => id !== dragId);
        const at = ids.indexOf(list.id) + (over && over.pos === 'after' ? 1 : 0);
        ids.splice(at, 0, dragId);
        const byId = Object.fromEntries(data.lists.map((l) => [l.id, l]));
        setData({ ...data, lists: ids.map((id) => byId[id]) });
        setDragId(null); setOver(null);
        await apiFetch('/api/tasks/lists/reorder', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids }) });
      },
    };
  }

  // ── My assignments: cross off ──
  async function toggleMine(t) {
    if (t.status === 'done') return;
    setCrossing((c) => ({ ...c, [t.id]: true }));
    const d = await setDone(t, true);
    setTimeout(() => {
      setCrossing((c) => { const n = { ...c }; delete n[t.id]; return n; });
      loadMine();
    }, 650);
    if (d) showToast(`Completed “${t.title}”${d.spawned ? ' · next one scheduled' : ''}`, async () => { await setDone(t, false); loadMine(); });
  }

  const mineByList = [];
  if (mine) {
    const m = new Map();
    for (const t of mine) {
      const k = t.list_id || 'inbox';
      if (!m.has(k)) { m.set(k, { key: k, name: t.list_name || 'Inbox', items: [] }); mineByList.push(m.get(k)); }
      m.get(k).items.push(t);
    }
  }
  const whoName = who ? ((people.find((p) => p.id === who) || {}).name || 'Someone') : 'You';

  return (
    <div className="hq-page-content hq-todo-page">
      <div className="hq-page-head hq-todo-head">
        <div>
          <h1>Tasks</h1>
          <p>Organize work into lists, assign it, and cross it off.</p>
        </div>
        {tab === 'lists' && !creating && <button className="hq-btn" onClick={() => setCreating(true)}>New list</button>}
      </div>

      <div className="hq-todo-tabs" role="tablist">
        <button role="tab" aria-selected={tab === 'lists'} className={tab === 'lists' ? 'is-active' : ''} onClick={() => setParams({})}>Lists</button>
        <button role="tab" aria-selected={tab === 'mine'} className={tab === 'mine' ? 'is-active' : ''} onClick={() => setParams({ tab: 'mine' })}>My assignments</button>
      </div>

      {tab === 'lists' && (
        <>
          {creating && (
            <form className="hq-panel hq-todo-newlist" onSubmit={createList}>
              <input className="hq-input" autoFocus placeholder="Name this list… e.g. Fall tour merch" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <textarea className="hq-input hq-todo-textarea" placeholder="Add extra details or attach a description (optional)" rows={2} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <div className="hq-todo-actions">
                <button className="hq-btn" type="submit" disabled={!form.name.trim()}>Add this list</button>
                <button type="button" className="hq-btn hq-btn-ghost" onClick={() => { setCreating(false); setForm({ name: '', description: '' }); setMsg(''); }}>Cancel</button>
              </div>
              {msg && <p className="hq-msg">{msg}</p>}
            </form>
          )}

          {!data ? <p className="hq-empty">Loading…</p> : (
            <div className="hq-todo-grid">
              <ListCard list={data.inbox} />
              {data.lists.map((l) => <ListCard key={l.id} list={l} drag={dragProps(l)} />)}
              {data.lists.length === 0 && !creating && (
                <button type="button" className="hq-todo-card hq-todo-card-new" onClick={() => setCreating(true)}>
                  <span>+ Make your first list</span>
                  <small>e.g. “Fall tour merch”, “Warehouse”, “Monthly close”</small>
                </button>
              )}
            </div>
          )}

          {data && data.archived_count > 0 && (
            <div className="hq-todo-archived">
              <button type="button" className="hq-todo-linkbtn" onClick={() => setShowArchived((s) => !s)}>
                {showArchived ? 'Hide' : 'Show'} archived lists ({data.archived_count})
              </button>
              {showArchived && archived && (
                <ul>
                  {archived.map((l) => (
                    <li key={l.id}>
                      <Link to={`/tasks/lists/${l.id}`}>{l.name}</Link>
                      <small>{l.done_count}/{(l.open_count || 0) + (l.done_count || 0)} completed</small>
                      <button type="button" className="hq-todo-linkbtn" onClick={() => unarchive(l.id)}>Unarchive</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}

      {tab === 'mine' && (
        <div className="hq-panel">
          <div className="hq-panel-head">
            <h2>{whoName === 'You' ? 'Assigned to you' : `Assigned to ${whoName}`}</h2>
            <select className="hq-select hq-select-sm" value={who} onChange={(e) => setWho(e.target.value)} aria-label="Whose assignments">
              <option value="">Me</option>
              {people.filter((p) => !user || p.id !== user.id).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          {!mine ? <p className="hq-empty">Loading…</p> : mine.length === 0 ? (
            <p className="hq-empty">Nothing open. Nice.</p>
          ) : (
            <div className="hq-todo-sections">
              {mineByList.map((s) => (
                <section key={s.key} className="hq-todo-section">
                  <h4 className="hq-todo-group-name"><Link to={`/tasks/lists/${s.key}`}>{s.name}</Link></h4>
                  {s.items.map((t) => (
                    <TodoRow key={t.id} task={t} crossing={!!crossing[t.id]} onToggle={() => toggleMine(t)} />
                  ))}
                </section>
              ))}
            </div>
          )}
        </div>
      )}
      {toast}
    </div>
  );
}
